/**
 * Ansicht „Inbetriebnahme" der Wartungsseite: mehrere Systeme auswählen und
 * in einem Zug einer Beauftragung zuordnen und auf proaktiv stellen.
 *
 * Der Anlass ist der Excel-Import: Importierte Systeme entstehen ohne
 * Beauftragung und mit ausgeschaltetem „proaktiv warten" — ihre Ampeln bleiben
 * grau, die Auslaufübersicht kennt sie nicht. Einzeln nachzuziehen hieße, für
 * jedes System die Kundenakte zu öffnen.
 *
 * Gruppiert wird nach KUNDE, und das ist keine Kosmetik: Eine Beauftragung
 * gehört zu genau einem Kunden. Eine kundenübergreifende Auswahl könnte gar
 * keine gemeinsame Beauftragung haben — der Dienst lehnt sie ab. Die
 * Gruppierung macht diese Grenze sichtbar, statt sie in eine Fehlermeldung zu
 * verschieben.
 *
 * Die Regel „proaktiv nur mit Beauftragung" (Spec E5) wird hier nur
 * VORWEGGENOMMEN (Knopf gesperrt, Hinweis sichtbar); durchgesetzt wird sie im
 * Dienst und vom CHECK `cs_proactive_needs_engagement`.
 */
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  Paper,
  Select,
  Skeleton,
  Stack,
  Switch,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { PendingEngagement, PendingSystemRow } from "@msc/shared";

import { useBulkUpdateSystems, usePendingSystems } from "../../hooks/useMaintenance.js";

interface CustomerGroup {
  customerId: string;
  customerName: string;
  systems: PendingSystemRow[];
  engagements: PendingEngagement[];
}

function groupByCustomer(
  items: PendingSystemRow[],
  engagements: PendingEngagement[],
): CustomerGroup[] {
  const groups = new Map<string, CustomerGroup>();
  for (const item of items) {
    let group = groups.get(item.customerId);
    if (!group) {
      group = {
        customerId: item.customerId,
        customerName: item.customerName,
        systems: [],
        engagements: engagements.filter((e) => e.customerId === item.customerId),
      };
      groups.set(item.customerId, group);
    }
    group.systems.push(item);
  }
  return [...groups.values()];
}

export function SystemsBulkTab() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = usePendingSystems();

  if (isError) {
    return (
      <Alert color="red" title={t("common.error")}>
        {t("common.errorLoading")}
      </Alert>
    );
  }
  if (isLoading || !data) return <Skeleton height={320} />;

  const groups = groupByCustomer(data.items, data.engagements);

  if (groups.length === 0) {
    return (
      <Text c="dimmed" ta="center" py="md">
        {t("pages.wartung.bulk.empty")}
      </Text>
    );
  }

  return (
    <Stack gap="md">
      <Text c="dimmed" size="sm">
        {t("pages.wartung.bulk.hint")}
      </Text>
      {groups.map((group) => (
        <CustomerBulkGroup key={group.customerId} group={group} />
      ))}
    </Stack>
  );
}

function CustomerBulkGroup({ group }: { group: CustomerGroup }) {
  const { t } = useTranslation();
  const bulkUpdate = useBulkUpdateSystems();

  const [selected, setSelected] = useState<string[]>([]);
  const [engagementId, setEngagementId] = useState<string | null>(null);
  const [proactive, setProactive] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const selectedSystems = group.systems.filter((s) => selected.includes(s.systemId));
  const allSelected = selected.length === group.systems.length && group.systems.length > 0;

  // „Proaktiv" braucht eine Beauftragung — entweder die hier gewählte oder
  // eine, die das System schon hat.
  const missingEngagement =
    proactive &&
    engagementId === null &&
    selectedSystems.some((s) => s.engagementId === null);

  const nothingToDo = engagementId === null && !proactive;
  const canApply =
    selectedSystems.length > 0 && !missingEngagement && !nothingToDo && !bulkUpdate.isPending;

  function toggle(systemId: string) {
    setSelected((prev) =>
      prev.includes(systemId) ? prev.filter((id) => id !== systemId) : [...prev, systemId],
    );
  }

  async function handleApply() {
    setError(null);
    try {
      await bulkUpdate.mutateAsync({
        systemIds: selected,
        // Nur mitschicken, was der Anwender tatsächlich gesetzt hat: ein
        // mitgeschicktes `null` würde eine bestehende Zuordnung LÖSCHEN.
        ...(engagementId !== null ? { engagementId } : {}),
        ...(proactive ? { proactiveMaintenance: true } : {}),
      });
      setSelected([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("pages.wartung.bulk.applyError"));
    }
  }

  return (
    <Paper p="md" withBorder>
      <Stack gap="sm">
        <Group justify="space-between" align="flex-end" wrap="wrap">
          <Text fw={600}>{group.customerName}</Text>
          <Group gap="sm" align="flex-end" wrap="wrap">
            <Select
              label={t("pages.wartung.bulk.engagementLabel")}
              placeholder={
                group.engagements.length === 0
                  ? t("pages.wartung.bulk.noEngagements")
                  : t("pages.wartung.bulk.engagementPlaceholder")
              }
              data={group.engagements.map((e) => ({
                value: e.id,
                label: `${e.name} · ${e.segmentName}${e.leadName ? ` · ${e.leadName}` : ""}`,
              }))}
              value={engagementId}
              onChange={setEngagementId}
              disabled={group.engagements.length === 0}
              clearable
              style={{ minWidth: 320 }}
            />
            <Switch
              label={t("pages.wartung.bulk.proactiveLabel")}
              checked={proactive}
              onChange={(e) => setProactive(e.currentTarget.checked)}
              mb={8}
            />
            <Button
              disabled={!canApply}
              loading={bulkUpdate.isPending}
              onClick={() => void handleApply()}
            >
              {t("pages.wartung.bulk.applyButton", { count: selectedSystems.length })}
            </Button>
          </Group>
        </Group>

        {missingEngagement && (
          <Alert color="orange" variant="light">
            {t("pages.wartung.bulk.needsEngagement")}
          </Alert>
        )}
        {error && (
          <Alert color="red" title={t("common.error")}>
            {error}
          </Alert>
        )}

        <Table highlightOnHover striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: 40 }}>
                <Checkbox
                  aria-label={t("pages.wartung.bulk.selectAllAria", {
                    customer: group.customerName,
                  })}
                  checked={allSelected}
                  indeterminate={selected.length > 0 && !allSelected}
                  onChange={(e) =>
                    setSelected(e.currentTarget.checked ? group.systems.map((s) => s.systemId) : [])
                  }
                />
              </Table.Th>
              <Table.Th>{t("pages.wartung.tableSystem")}</Table.Th>
              <Table.Th>{t("pages.wartung.bulk.tableStage")}</Table.Th>
              <Table.Th>{t("pages.wartung.tableEngagement")}</Table.Th>
              <Table.Th>{t("pages.wartung.bulk.tableProactive")}</Table.Th>
              <Table.Th>{t("pages.wartung.bulk.tableSoftwareCount")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {group.systems.map((system) => (
              <Table.Tr key={system.systemId}>
                <Table.Td>
                  <Checkbox
                    aria-label={system.systemName}
                    checked={selected.includes(system.systemId)}
                    onChange={() => toggle(system.systemId)}
                  />
                </Table.Td>
                <Table.Td>{system.systemName}</Table.Td>
                <Table.Td>{t(`maintenance.stage.${system.stage}`)}</Table.Td>
                <Table.Td>
                  {system.engagementName ?? (
                    <Text size="sm" c="dimmed">
                      {t("pages.wartung.noEngagement")}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Group gap={6} wrap="nowrap">
                    <Badge
                      size="sm"
                      variant="light"
                      color={system.proactiveMaintenance ? "green" : "gray"}
                    >
                      {system.proactiveMaintenance ? t("common.yes") : t("common.no")}
                    </Badge>
                    {/* Ohne diesen Merker sähe ein System, dem der Schalter
                        beim Wegfall der Beauftragung selbsttätig
                        abhandengekommen ist, hier genauso aus wie ein frisch
                        importiertes, das nie proaktiv war — und genau hier
                        wird er wieder gesetzt. */}
                    {system.proactiveAutoDisabledAt !== null && (
                      <Tooltip
                        label={t("maintenance.card.proactiveAutoDisabledHint")}
                        multiline
                        w={280}
                      >
                        <Badge size="sm" variant="light" color="orange">
                          {t("maintenance.card.proactiveAutoDisabled")}
                        </Badge>
                      </Tooltip>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>{system.softwareCount}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>
    </Paper>
  );
}
